import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Download, ExternalLink, Receipt } from "lucide-react";
import { SubscriptionService } from "@/lib/subscription/subscription.service";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

interface Invoice {
	id: string;
	number: string | null;
	amount: number;
	currency: string;
	status: string | null;
	created: number;
	dueDate: number | null;
	paidAt: number | null;
	hostedInvoiceUrl: string | null;
	invoicePdf: string | null;
	description: string | null;
	periodStart: number;
	periodEnd: number;
}

/**
 * Invoice Management Component
 * Displays user's past invoices with download/view options
 */
const InvoiceManagement = () => {
	const { t } = useTranslation();
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchInvoices = async () => {
			try {
				setLoading(true);
				const response = await SubscriptionService.getInvoices();
				if (response.success) {
					setInvoices(response.invoices);
				}
			} catch (error) {
				console.error("Failed to fetch invoices:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchInvoices();
	}, []);

	const formatAmount = (amount: number, currency: string) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency.toUpperCase(),
		}).format(amount / 100);
	};

	const formatDate = (timestamp: number) => {
		return new Date(timestamp * 1000).toLocaleDateString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getStatusColor = (status: string | null) => {
		switch (status) {
			case "paid":
				return "text-emerald-400";
			case "open":
				return "text-amber-400";
			case "void":
			case "uncollectible":
				return "text-muted-foreground";
			default:
				return "text-foreground";
		}
	};

	// Don't show if no invoices
	if (!loading && invoices.length === 0) {
		return null;
	}

	return (
		<div className="w-full bg-card border border-border rounded-xl p-6 shadow-sm">
			<div className="flex items-center gap-2 mb-4">
				<Receipt className="h-5 w-5 text-primary" />
				<h3 className="text-lg font-semibold text-foreground">
					{t("pricing.invoices.title")}
				</h3>
			</div>

			{loading ? (
				<div className="flex justify-center items-center py-8">
					<Loader2 className="h-6 w-6 animate-spin text-primary" />
				</div>
			) : (
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("pricing.invoices.table.date")}</TableHead>
								<TableHead>{t("pricing.invoices.table.number")}</TableHead>
								<TableHead>{t("pricing.invoices.table.amount")}</TableHead>
								<TableHead>{t("pricing.invoices.table.status")}</TableHead>
								<TableHead className="text-right">
									{t("pricing.invoices.table.actions")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{invoices.map((invoice) => (
								<TableRow key={invoice.id}>
									<TableCell className="text-sm">
										{formatDate(invoice.created)}
									</TableCell>
									<TableCell className="text-sm font-mono">
										{invoice.number || invoice.id.substring(0, 12)}
									</TableCell>
									<TableCell className="text-sm font-medium">
										{formatAmount(invoice.amount, invoice.currency)}
									</TableCell>
									<TableCell>
										<span
											className={`text-xs font-medium ${getStatusColor(invoice.status)}`}
										>
											{invoice.status
												? t(`pricing.invoices.status.${invoice.status}`)
												: "-"}
										</span>
									</TableCell>
									<TableCell className="text-right">
										<div className="flex items-center justify-end gap-2">
											{invoice.hostedInvoiceUrl && (
												<Button
													variant="ghost"
													size="sm"
													asChild
													className="h-8 px-2"
												>
													<a
														href={invoice.hostedInvoiceUrl}
														target="_blank"
														rel="noopener noreferrer"
														title={t("pricing.invoices.actions.view")}
													>
														<ExternalLink className="h-4 w-4" />
													</a>
												</Button>
											)}
											{invoice.invoicePdf && (
												<Button
													variant="ghost"
													size="sm"
													asChild
													className="h-8 px-2"
												>
													<a
														href={invoice.invoicePdf}
														target="_blank"
														rel="noopener noreferrer"
														download
														title={t("pricing.invoices.actions.download")}
													>
														<Download className="h-4 w-4" />
													</a>
												</Button>
											)}
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{invoices.length === 0 && (
						<div className="text-center py-8 text-muted-foreground text-sm">
							{t("pricing.invoices.noInvoices")}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default InvoiceManagement;
